import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectSidebarOpen, setSidebarOpen } from '@/store/slices/uiSlice';

import { AppHeader } from './AppHeader';
import { Sidebar } from './Sidebar';
import { ProfileCompletionBanner } from '@/components/profile/ProfileCompletionBanner';
import { SessionTimeoutWarning } from '@/components/security/SessionTimeoutWarning';

const DRAWER_WIDTH = 280;

export const DashboardLayout: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector(selectSidebarOpen);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  }, [isMobile, dispatch]);

  const handleSidebarToggle = () => {
    dispatch(setSidebarOpen(!sidebarOpen));
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        onToggle={handleSidebarToggle}
        width={DRAWER_WIDTH}
        isMobile={isMobile}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: theme.transitions.create(['margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: {
            xs: 0,
            md: sidebarOpen ? 0 : `-${DRAWER_WIDTH}px`,
          },
        }}
      >
        {/* Header */}
        <AppHeader onMenuClick={handleSidebarToggle} />

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            backgroundColor: 'background.default',
            p: { xs: 2, md: 3 },
          }}
        >
          {/* Profile Completion Banner */}
          <ProfileCompletionBanner />
          
          <Outlet />
        </Box>
      </Box>
      
      {/* Session Timeout Warning - Global overlay */}
      <SessionTimeoutWarning />
    </Box>
  );
};